import axios from 'axios';

// Базова адреса API. Для локальної розробки задай REACT_APP_API_URL
// у .env.local (напр. http://localhost:8000), інакше збірка ходить у прод.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://parser.mne.cc/api';

const apiClient = axios.create({ 
    baseURL: API_BASE_URL,
    timeout: 600000
});

// Attach JWT token to every request
apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// On 401, clear token (session expired / invalid)
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            invalidateCache();
        }
        return Promise.reject(error);
    }
);

// ── Кеш відповідей ──────────────────────────────────────────
// Перехід між сторінками раніше щоразу перезапитував усе з нуля: зведення,
// фасети, списки. Тепер відповіді GET зберігаються в пам'яті вкладки:
//  * свіжа (молодша за ttl) — віддається одразу, без запиту;
//  * застаріла — сторінка показує її миттєво (peekCache), а запит у фоні
//    підтягує нову (як stale-while-revalidate);
//  * однакові запити, що летять одночасно, зливаються в один.
// Після змін (завантаження прайсу, зіставлення, збір) відповідні ключі
// скидаються через invalidateCache(префікс URL).
const SEC = 1000;
const MIN = 60 * SEC;
const CACHE_LIMIT = 300;
const cache = new Map();   // key -> { at, data, promise }

const cacheKey = (url, params) => {
    const entries = Object.entries(params || {})
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .sort(([a], [b]) => a.localeCompare(b));
    return `${url}?${JSON.stringify(entries)}`;
};

const cachedGet = (url, params, ttl) => {
    const key = cacheKey(url, params);
    const hit = cache.get(key);
    if (hit && hit.data !== undefined && Date.now() - hit.at < ttl) {
        return Promise.resolve(hit.data);
    }
    if (hit && hit.promise) return hit.promise;

    const promise = apiClient.get(url, { params }).then(response => {
        cache.delete(key);   // перевставка — щоб порядок Map був «від найстаріших»
        cache.set(key, { at: Date.now(), data: response.data });
        if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
        return response.data;
    }).catch(err => {
        const cur = cache.get(key);
        if (cur && cur.promise === promise) {
            if (cur.data === undefined) cache.delete(key);
            else cache.set(key, { at: cur.at, data: cur.data });
        }
        throw err;
    });
    cache.set(key, { ...(hit || {}), promise });
    return promise;
};

// Запит повз кеш (і серверний теж: fresh=true) — відповідь кладеться в кеш
// під звичайним ключем, щоб наступні переходи її вже бачили.
const freshGet = (url, params) =>
    apiClient.get(url, { params: { ...params, fresh: true } }).then(response => {
        cache.delete(cacheKey(url, params));
        cache.set(cacheKey(url, params), { at: Date.now(), data: response.data });
        return response.data;
    });

// Остання відома відповідь (навіть застаріла) — для миттєвого першого рендера.
export const peekCache = (url, params) => cache.get(cacheKey(url, params))?.data;

// Скинути кеш: усе або лише ключі, що починаються з цих URL.
export const invalidateCache = (...prefixes) => {
    [...cache.keys()].forEach(key => {
        if (!prefixes.length || prefixes.some(p => key.startsWith(p))) cache.delete(key);
    });
};

export const authAPI = {
    login: async (email, password) => {
        const response = await apiClient.post('/login', { email, password });
        return response.data;  // { access_token, token_type, user }
    },
    register: async (email, password) => {
        const response = await apiClient.post('/register', { email, password });
        return response.data;  // { message, user_id, status, is_admin }
    },
    getCurrentUser: async () => {
        const response = await apiClient.get('/me');
        return response.data;
    }
};

// Значення для фільтрів беремо з єдиного шару (/offers/facets), а не вигрібанням
// усього каталогу через /get_tires: раніше сторінка тягла в браузер усі ~146 тис.
// рядків лише щоб зібрати списки, а після переводу автозбору на CompetitorOffer
// легасі-таблиця Tire перестала оновлюватись і списки б застигли.
// Порядок значень тут не важливий — HomePage сортує їх сама.
let facetsCache = null;
const getInfoshinaFacets = async () => {
    if (facetsCache) return facetsCache;
    const response = await apiClient.get('/offers/facets', { params: { source: 'infoshina' } });
    facetsCache = response.data || {};
    return facetsCache;
};

export const fetchBrands = async () => {
    const { brands = [] } = await getInfoshinaFacets();
    return { brands: brands.map(b => ({ brand_name: b })) };
};

export const fetchTireWidths = async () => {
    const { widths = [] } = await getInfoshinaFacets();
    return { widths: widths.map(w => ({ width: w })) };
};

export const fetchTireProfils = async () => {
    const { profils = [] } = await getInfoshinaFacets();
    return { profils: profils.map(p => ({ profil: p })) };
};

export const fetchTireDiametrs = async () => {
    const { diametrs = [] } = await getInfoshinaFacets();
    return { diametrs: diametrs.map(d => ({ diametr: d })) };
};

export const fetchTireModels = async () => {
    const { models = [] } = await getInfoshinaFacets();
    return { models: models.map(m => ({ model: m })) };
};

export const scrapeTires = async (url, pageCount = 1, filters = {}) => {
    const response = await apiClient.post('/insert_tires', { url, page_count: pageCount });
    const task_id = response.data.task_id;
    return await pollTaskStatus(task_id);
};

const pollTaskStatus = async (task_id) => {
    for (let i = 0; i < 120; i++) {
        await new Promise(r => setTimeout(r, 5000));
        const response = await apiClient.get(`/task_status/${task_id}`);
        const status = response.data;
        if (status.status === 'completed') {
            facetsCache = null;   // після збору могли з'явитись нові бренди/розміри
            const resultResp = await apiClient.get(`/task_result/${task_id}`);
            return { data: resultResp.data.tires || [] };
        }
        if (status.status === 'failed') throw new Error(status.error || 'Scraping failed');
        if (status.status === 'not_found') throw new Error('Task not found');
    }
    throw new Error('Timeout');
};

export const addTiresToDatabase = async (tires) => {
    const response = await apiClient.post('/save_tires', { tires });
    return response.data;
};

export const fetchAllTires = async (onProgress = null) => {
    const response = await apiClient.post('/fetch_all');
    const task_id = response.data.task_id;

    for (let i = 0; i < 600; i++) {
        await new Promise(r => setTimeout(r, 5000));
        const statusResp = await apiClient.get(`/task_status/${task_id}`);
        const status = statusResp.data;

        if (onProgress) onProgress(status);

        if (status.status === 'completed') {
            facetsCache = null;   // після збору могли з'явитись нові бренди/розміри
            const resultResp = await apiClient.get(`/task_result/${task_id}`);
            return { data: resultResp.data.tires || [], total_tires: status.total_tires || 0 };
        }
        if (status.status === 'failed') throw new Error(status.error || 'Fetch all failed');
        if (status.status === 'not_found') throw new Error('Task not found');
    }
    throw new Error('Timeout');
};

export const fetchTirePriceHistory = async (tireId) => {
    const response = await apiClient.get(`/get_tire_price_history/${tireId}`);
    return response.data;  // { price_history: [ {recorded_at, price}, ... ] }
};

export const mappingAPI = {
    // Дані оффера підтягуються для БУДЬ-ЯКОГО джерела. Старий /mapping/list
    // обогащав лише infoshina/ukrshina і більше не використовується.
    listAll: (opts = {}) => {
        if (opts.fresh) invalidateCache('/mappings');
        return cachedGet('/mappings', {}, 30 * SEC);  // { mappings: [{my_id, sources:{...}}], total }
    },
    add: async (myId, source, competitorId) => {
        const response = await apiClient.post('/mapping/add', {
            my_id: myId, source, competitor_id: competitorId
        });
        invalidateCache('/mappings', '/own_products');
        return response.data;
    },
    bulk: async (triples) => {
        // triples: [[my_id, source, competitor_id], ...]
        const response = await apiClient.post('/mapping/bulk', { triples });
        invalidateCache('/mappings', '/own_products');
        return response.data;  // { message, count }
    },
    remove: async (myId, source = null) => {
        const response = await apiClient.post('/mapping/delete', { my_id: myId, source });
        invalidateCache('/mappings', '/own_products');
        return response.data;
    }
};

// Зіставлення назв моделей (ML): пари "наша модель ↔ модель конкурента".
export const modelAliasAPI = {
    list: (status = 'review', { search = '', limit = 50, offset = 0 } = {}) =>
        // { total, counts: {status: n}, aliases: [...], report }
        cachedGet('/model_aliases', { status, search, limit, offset }, 30 * SEC),
    decide: async (id, decision) => {
        // decision: 'confirm' | 'reject' | 'reset'
        const response = await apiClient.post(`/model_aliases/${id}/decide`, { decision });
        invalidateCache('/model_aliases', '/own_products');
        return response.data;  // { status, links }
    },
    rebuild: async () => {
        const response = await apiClient.post('/model_aliases/rebuild');
        invalidateCache('/model_aliases', '/own_products');
        return response.data;
    },
};

// Прибрано як мертвий код: ukrshinaAPI, competitorsAPI, ukrshinaFetchAPI,
// comparePrices, fetchTires. Це були обгортки над окремими ендпоінтами кожного
// джерела (/ukrshina/*, /competitor_*, /fetch_tshina, /compare_prices) — усе
// це замінив generic-шар offersAPI + comparePricesV2 нижче. Самі ендпоінти на
// бекенді поки живі, але фронт до них більше не звертається.

export const adminAPI = {
    getUsers: async () => {
        const response = await apiClient.get('/admin/users');
        // Normalize to what AdminPanel expects: { data, total, total_pages }
        const users = response.data.users || [];
        return { data: users, total: response.data.total || users.length, total_pages: 1 };
    },
    approveUser: async (userId, status = 'approved') => {
        // status comes from AdminPanel: 'approved' or 'rejected'
        if (status === 'rejected') {
            const response = await apiClient.post('/admin/reject', { user_id: userId });
            return response.data;
        }
        const response = await apiClient.post('/admin/approve', { user_id: userId });
        return response.data;
    }
};

// ── Unified offers layer (Phase 1 backend) ──────────────────
// Generic, source-agnostic replacement for ukrshinaAPI/competitorsAPI/
// ukrshinaFetchAPI above. Those stay untouched and working — pages migrate to
// this one at their own pace.
export const offersAPI = {
    // GET /offers/sources -> { sources: [{source_key, title, base_url, is_active}] }
    sources: () => cachedGet('/offers/sources', {}, 10 * MIN),
    // GET /offers?source=&search=&season=&width=&profil=&diametr=&limit=&offset= -> { total, offers }
    list: (params = {}) => cachedGet('/offers', params, 30 * SEC),
    // GET /offers/stats?source= -> { total, with_price, out_of_stock, last_updated, seasons }
    // fresh — після збору: повз кеш і тут, і на сервері.
    stats: (source = '', { fresh = false } = {}) => {
        return fresh ? freshGet('/offers/stats', { source }) : cachedGet('/offers/stats', { source }, MIN);
    },
    // GET /offers/dashboard -> { sources: [{...stats, recent_changes}], recent_hours }
    dashboard: (recentHours = 24) => cachedGet('/offers/dashboard', { recent_hours: recentHours }, MIN),
    // GET /offers/facets?source= -> { brands, widths, profils, diametrs, seasons }
    facets: (source = '') => cachedGet('/offers/facets', { source }, 15 * MIN),
    // GET /offers/{id}/price_history -> { price_history: [{recorded_at, price}] }
    priceHistory: (offerId) => cachedGet(`/offers/${offerId}/price_history`, {}, MIN),
    // POST /offers/fetch/{source} -> { task_id } — единый диспетчер сбора для всех 4 источников
    fetch: async (source) => {
        const response = await apiClient.post(`/offers/fetch/${source}`);
        return response.data;
    },
    // Після завершення збору: нові ціни мають з'явитись одразу.
    afterFetch: () => invalidateCache('/offers', '/own_products', '/alerts'),
    // GET /task_status/{task_id} -> тот же общий эндпоинт статуса задач, что и у остальных сборщиков
    taskStatus: async (taskId) => {
        const response = await apiClient.get(`/task_status/${taskId}`);
        return response.data;
    }
};

// Персистентный каталог своих товаров (раньше жил только в Excel на клиенте)
export const ownProductsAPI = {
    upload: async (items) => {
        const response = await apiClient.post('/own_products/upload', { items });
        invalidateCache('/own_products');
        return response.data;
    },
    // Завантаження вивантаження як є: розбір і згортання по товару робить
    // сервер, щоб правила не дублювались у JS і не розходились із бекендом.
    async importRows(rows, automap = true) {
        const response = await apiClient.post('/own_products/import', { rows, automap });
        invalidateCache('/own_products');
        return response.data;
    },
    // Файл формує сервер: заливку клітинок бібліотека xlsx у браузері не вміє.
    // Через axios, а не звичайним посиланням, бо ендпоінт вимагає токен.
    async exportXlsx(freshDays = 21) {
        const response = await apiClient.get('/own_products/export.xlsx', {
            params: { fresh_days: freshDays },
            responseType: 'blob',
        });
        return response.data;
    },
    async exportAll(freshDays = 21) {
        const response = await apiClient.get('/own_products/export', { params: { fresh_days: freshDays } });
        return response.data;  // { message, saved }
    },
    // position — фільтр за позицією відносно ринку: matched, unmatched, cheaper, even, dearer.
    list: (search = '', limit = 100, offset = 0, position = '') =>
        // { total, products: [{..., competitors, market_min, market_gap_pct, market_pos}] }
        cachedGet('/own_products', { search, limit, offset, position }, 30 * SEC),
    peekList: (search = '', limit = 100, offset = 0, position = '') =>
        peekCache('/own_products', { search, limit, offset, position }),
    // Скільки товарів дешевші / в ринку / дорожчі за ринок і середня маржа.
    // { total, matched, unmatched, cheaper, even, dearer, avg_margin_pct, updated_at }
    // fresh — після завантаження прайсу: повз кеш і тут, і на сервері.
    summary: ({ fresh = false } = {}) => {
        return fresh ? freshGet('/own_products/summary', {}) : cachedGet('/own_products/summary', {}, MIN);
    },
    // { own: [...], competitors: {source: {title, origin, history: [...]}} }
    priceHistory: (myId) => cachedGet(`/own_products/${myId}/price_history`, {}, MIN),
};

// Значимі зміни цін конкурентів по наших товарах (price_monitor.build_alerts):
// конкурент став дешевшим за нас, дешевший зник, різке зниження/зростання.
export const alertsAPI = {
    // { total, counts, alerts: [...] }
    list: ({ hours = 72, limit = 10, kind = '', source = '' } = {}) =>
        cachedGet('/alerts', { hours, limit, kind, source }, MIN),
};

// Динаміка цін конкурента між датами (сторінка /price-dynamics).
// dates — масив 'РРРР-ММ-ДД', на сервер ідуть через кому.
export const priceDynamicsAPI = {
    // { min_date, max_date }
    range: (source) => cachedGet('/price_dynamics/range', { source }, 10 * MIN),
    // Важкий розрахунок на сервері — повернення на сторінку не має його повторювати.
    summary: (source, dates, completeOnly = true) =>
        cachedGet('/price_dynamics', { source, dates: dates.join(','), complete_only: completeOnly }, 5 * MIN),
    peekSummary: (source, dates, completeOnly = true) =>
        peekCache('/price_dynamics', { source, dates: dates.join(','), complete_only: completeOnly }),
    // Через axios, а не посиланням, бо ендпоінт вимагає токен.
    exportXlsx: async (source, dates, completeOnly = true) => {
        const response = await apiClient.get('/price_dynamics/export.xlsx', {
            params: { source, dates: dates.join(','), complete_only: completeOnly },
            responseType: 'blob',
        });
        return response.data;
    },
};

// Generic-версия comparePrices — перебирает ВСЕ источники, присутствующие в
// TireMapping, вместо хардкода infoshina/ukrshina в comparePrices() выше.
// Отдельная функция (v2), пока PriceComparisonPage не переведён на неё.
export const comparePricesV2 = async (items) => {
    const response = await apiClient.post('/compare_prices/v2', { items });
    return response.data;  // { results, total, matched, no_mapping }
};
